import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Alert, Logo } from '@heroui/react';
import { useAuthStore } from '../../stores/authStore';
import { useUiStore } from '../../stores/uiStore';
import { useTrack } from '../../telemetry';
import { API_BASE_URL } from '../../lib/axios';
import { ssoApi } from '../../api/ssoApi';
import { ssoStartUrl } from './ssoNavigation';
import { safeNextPath } from '../../lib/nextPath';
import { SIGNUP_ENABLED } from '../../lib/env';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const theme = useUiStore((s) => s.resolvedTheme);
  const track = useTrack();
  const [searchParams] = useSearchParams();
  // Phase 45.1b — a failed IdP round-trip lands back here with a
  // display-safe reason (`/login?ssoError=…`, set by the API's ACS/OIDC
  // failure redirect).
  const ssoRedirectError = searchParams.get('ssoError');
  // Phase 49 — where ProtectedRoute wanted to send us before login.
  // Sanitized against open-redirect; defaults to the command center.
  const nextPath = safeNextPath(searchParams.get('next'));
  const [error, setError] = useState<string | null>(null);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await login(data.email, data.password);
      // Phase 45.1b — the org enforces SSO: password was correct, but
      // the session must come from the IdP. Full browser navigation
      // (not an XHR) so the 302 chain can land on the IdP login page.
      if (result.ssoRequired && result.connectionId) {
        window.location.assign(ssoStartUrl(API_BASE_URL, result.connectionId, '/'));
        return;
      }
      track('dashboard.auth.login', {
        context: { mfaRequired: result.mfaRequired },
      });
      if (result.mfaRequired) {
        // Carry the destination across the MFA step too.
        navigate(`/mfa${nextPath === '/' ? '' : `?next=${encodeURIComponent(nextPath)}`}`);
      } else {
        navigate(nextPath);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : // A plain Error is one the auth store threw on purpose (e.g. an
            // MFA challenge with no mfaToken) — show it rather than blaming
            // the password. Axios errors keep the generic fallback.
            err instanceof Error && !('isAxiosError' in err)
            ? err.message
            : null;
      setError(message || 'Incorrect email or password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Phase 45.1b — explicit "sign in with SSO" path: look up the typed
  // email's domain against active connections; if one exists, hand the
  // browser to the IdP without requiring a password.
  const onUseSso = async () => {
    setSsoError(null);
    const email = (getValues('email') ?? '').trim();
    if (!email || !email.includes('@')) {
      setSsoError('Enter your work email above first, then try again.');
      return;
    }
    setIsDiscovering(true);
    try {
      const result = await ssoApi.discover(email);
      if (result.sso) {
        window.location.assign(ssoStartUrl(API_BASE_URL, result.connectionId, '/'));
        return;
      }
      setSsoError("Single sign-on isn't configured for this email domain");
    } catch {
      setSsoError("Single sign-on isn't configured for this email domain");
    } finally {
      setIsDiscovering(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={140} onDark={theme === 'dark'} />
          <h1 className="text-default-500 text-sm font-normal">Sign in to your account</h1>
        </div>

        {ssoRedirectError && !error && (
          <Alert status="danger">
            {ssoRedirectError}
          </Alert>
        )}

        {error && (
          <Alert status="danger">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Input
              type="email"
              placeholder="Email address"
              autoComplete="email"
              autoFocus
              fullWidth
              {...register('email')}
            />
            {errors.email && (
              <p className="text-danger text-xs">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              fullWidth
              {...register('password')}
            />
            {errors.password && (
              <p className="text-danger text-xs">{errors.password.message}</p>
            )}
          </div>

          <Button
            type="submit"
            style={{
              width: '100%',
              // Brand-aligned colors (navy bg + gold text) — matches the
              // logo glyph and reads cleanly in both light + dark mode
              // against `bg-default-50`. Inline so we don't sweep the
              // whole dashboard's `--color-accent` token in this round.
              backgroundColor: '#0D1B2A',
              color: '#F0D060',
              borderColor: '#0D1B2A',
              opacity: isSubmitting ? 0.7 : 1,
            }}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-primary text-sm font-medium hover:underline"
            >
              Forgot password?
            </Link>
          </div>

          <div className="space-y-1 text-center">
            <button
              type="button"
              onClick={() => void onUseSso()}
              disabled={isDiscovering}
              className="text-default-500 text-sm font-medium hover:text-default-700 hover:underline disabled:opacity-60"
            >
              {isDiscovering ? 'Checking…' : 'Use single sign-on (SSO)'}
            </button>
            {ssoError && <p className="text-danger text-xs">{ssoError}</p>}
          </div>
        </form>

        {SIGNUP_ENABLED && (
          <p className="text-center text-sm text-default-500">
            New to Commerce OS?{' '}
            <Link to="/register" className="text-primary font-medium">
              Create an account
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
